import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm";

export const emptyContract = () => ({
  contacts_urgence: [{ nom: "", telephone: "", lien: "" }, { nom: "", telephone: "", lien: "" }],
  terms_accepted: false, waiver_accepted: false, photo_consent: false, signature_name: "",
});

export function contractIsValid(c) {
  const u = c.contacts_urgence[0] || {};
  return Boolean(u.nom?.trim() && u.telephone?.trim() && c.terms_accepted && c.waiver_accepted && c.signature_name.trim().length >= 3);
}

export function ContractStep({ form, total, contract, setContract, onBack, onSubmit, busy }) {
  const [texts, setTexts] = useState(null);
  useEffect(() => {
    axios.get(`${API}/samourais/public/contract-conditions`).then((r) => setTexts(r.data)).catch(() => setTexts({ conditions: [], waiver: "" }));
  }, []);
  const setC = (k, v) => setContract((p) => ({ ...p, [k]: v }));
  const setU = (i, k, v) => setContract((p) => {
    const cu = p.contacts_urgence.map((c, j) => (j === i ? { ...c, [k]: v } : c));
    return { ...p, contacts_urgence: cu };
  });
  const valid = contractIsValid(contract);

  return (
    <div className="space-y-5" data-testid="samourais-contract-step">
      <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm flex flex-wrap justify-between gap-2" data-testid="samourais-contract-summary">
        <span><b>{form.child_name}</b>{form.child_age ? ` (${form.child_age} ans)` : ""} · parent : {form.parent_name}</span>
        <span className="text-zinc-400">{form.parent_email}</span>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Contacts d'urgence <span className="text-zinc-500">(1 requis)</span></label>
        {contract.contacts_urgence.map((c, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 mb-2">
            <input data-testid={`samourais-urgence-nom-${i}`} placeholder={i === 0 ? "Nom *" : "Nom (optionnel)"} value={c.nom} onChange={(e) => setU(i, "nom", e.target.value)} className={`${inputCls} col-span-3 sm:col-span-1`} />
            <input data-testid={`samourais-urgence-tel-${i}`} type="tel" placeholder={i === 0 ? "Téléphone *" : "Téléphone"} value={c.telephone} onChange={(e) => setU(i, "telephone", e.target.value)} className={`${inputCls} col-span-2 sm:col-span-1`} />
            <input data-testid={`samourais-urgence-lien-${i}`} placeholder="Lien (grand-parent…)" value={c.lien} onChange={(e) => setU(i, "lien", e.target.value)} className={`${inputCls} col-span-1`} />
          </div>
        ))}
      </div>

      <div className="border border-white/15 bg-white/[0.03] rounded-xl" data-testid="samourais-terms-box">
        <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-widest text-zinc-400">Conditions du programme</div>
        <div className="px-4 pb-3 max-h-44 overflow-y-auto text-[12px] leading-relaxed text-white/70 space-y-2" data-testid="samourais-terms-text">
          {!texts && <p>Chargement…</p>}
          {texts?.conditions?.map((t, i) => <p key={i}>{i + 1}. {t}</p>)}
        </div>
        <label className="flex items-start gap-2 px-4 pb-4 text-sm cursor-pointer">
          <input type="checkbox" data-testid="samourais-accept-terms" checked={contract.terms_accepted} onChange={(e) => setC("terms_accepted", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#D90429]" />
          <span>J'ai lu et j'accepte les conditions du programme Petits Samouraïs.</span>
        </label>
      </div>

      <div className="border border-[#C9A961]/40 bg-[#C9A961]/[0.05] rounded-xl p-4" data-testid="samourais-waiver-box">
        <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-2">Décharge de responsabilité (parent / tuteur)</div>
        <p className="text-[12px] leading-relaxed text-white/70 mb-3">{texts?.waiver}</p>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="samourais-accept-waiver" checked={contract.waiver_accepted} onChange={(e) => setC("waiver_accepted", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C9A961]" />
          <span>Je suis le parent ou tuteur légal de l'enfant et j'accepte la décharge ci-dessus.</span>
        </label>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="samourais-photo-block">
        <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-2">Photos et vidéos (optionnel)</div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="samourais-accept-photo" checked={!!contract.photo_consent} onChange={(e) => setC("photo_consent", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C9A961]" />
          <span>J'autorise l'Institut Impact Élite Martial à utiliser des photos et vidéos de mon enfant et de moi prises au dojo pour présenter ses programmes (réseaux sociaux, site web, imprimés), sans rémunération et sans nom de famille. Retrait possible en tout temps par courriel.</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Signature électronique <span className="text-zinc-500">(tape ton nom complet)</span></label>
        <input data-testid="samourais-signature-name" placeholder={form.parent_name || "Nom complet du parent"} value={contract.signature_name}
          onChange={(e) => setC("signature_name", e.target.value)}
          className={`${inputCls} w-full font-serif italic text-lg`} />
        <p className="text-[11px] text-zinc-500 mt-1">Signé le {new Date().toLocaleDateString("fr-CA")} · une copie PDF du contrat est conservée dans ton dossier au dojo.</p>
      </div>

      <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-4">
        <span className="text-zinc-400">Total</span>
        <span className="text-2xl font-bold" data-testid="samourais-total">{total.toFixed(2)} $</span>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onBack} data-testid="samourais-back-btn" className="rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 px-4 py-3.5 text-sm">← Retour</button>
        <button type="button" onClick={onSubmit} disabled={busy || !valid} data-testid="samourais-submit"
          className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 py-3.5 font-semibold transition-colors disabled:opacity-50">
          {busy ? "Traitement…" : total > 0 ? "Signer et payer" : "Signer et confirmer"}
        </button>
      </div>
      <p className="text-center text-xs text-zinc-500">Paiement sécurisé par Stripe · (819) 527-9446 · <a href="/politiques" target="_blank" rel="noreferrer" className="underline">Politiques</a> · <a href="/notre-engagement" target="_blank" rel="noreferrer" className="underline">Notre engagement</a></p>
    </div>
  );
}
